<script setup lang="ts">
import { computed, ref } from "vue";
import PageContainer from "~/components/ui/PageContainer.vue";
import { buildComputerCollectionSchema } from "~/features/computer/data/seo/computer-schema";
import { practiceShortQuestions, practiceUnits } from "~/features/computer/data/practice/practice.data";

const selectedUnit = ref<(typeof practiceUnits)[number]["value"]>("all");
const selectedTopic = ref<string>("all");
const mode = ref<"hide" | "show">("hide");
const openCard = ref<string | null>(null);

const topicOptions = computed(() => {
  const items = practiceShortQuestions
    .filter((question) => selectedUnit.value === "all" || question.unit === selectedUnit.value)
    .map((question) => ({ value: question.topic, label: question.topicLabel }));

  const unique = items.filter((item, index, all) => all.findIndex((candidate) => candidate.value === item.value) === index);
  return [{ value: "all", label: "All" }, ...unique];
});

const filteredQuestions = computed(() => practiceShortQuestions.filter((question) => (
  (selectedUnit.value === "all" || question.unit === selectedUnit.value)
  && (selectedTopic.value === "all" || question.topic === selectedTopic.value)
)));

const faqs = [
  {
    question: "Practice short questions kis liye useful hain?",
    answer:
      "Yeh page flashcard-style revision ke liye useful hai. Aap pehle sawal dekh kar khud answer sochte ho, phir answer reveal karke self-check karte ho.",
  },
  {
    question: "Kya short questions practice me topic filter milta hai?",
    answer:
      "Haan, is page par chapter aur topic dono filters milte hain taake aap focused revision kar sako.",
  },
  {
    question: "Practice short questions aur chapter short questions me kya farq hai?",
    answer:
      "Practice page mixed self-testing ke liye hai, jab ke chapter short questions page chapter-wise organized prep ke liye better hota hai.",
  },
];

function toggleCard(id: string) {
  openCard.value = openCard.value === id ? null : id;
}

usePageSeo({
  title: "Practice Short Questions | 11th Class Computer",
  description: "11th class computer ke mixed short questions ko topic filters aur hide/show answer mode ke sath revise karo.",
  keywords: "11th class computer short questions practice, first year computer short questions revision, flashcard computer short questions",
  faqs,
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th Computer", item: "/11th/computer/" },
    { name: "Practice Mode", item: "/11th/computer/practice/" },
    { name: "Short Questions", item: "/11th/computer/practice/short-questions/" },
  ],
  schemaType: "CollectionPage",
  schemaProperties: buildComputerCollectionSchema({
    about: "11th Class Computer Practice Short Questions",
    items: [
      { name: "Short Questions Hub", path: "/11th/computer/short-questions/" },
      { name: "Pairing Scheme", path: "/11th/computer/pairing-scheme/" },
      { name: "Practice MCQs", path: "/11th/computer/practice/mcqs/" },
      { name: "Practice Long Questions", path: "/11th/computer/practice/long-questions/" },
    ],
  }),
});
</script>

<template>
  <PageContainer>
    <div class="space-y-8">
      <section class="space-y-4 rounded-3xl bg-slate-950 p-6 text-white">
        <h1 class="text-2xl font-bold tracking-tight">All Short Questions</h1>
        <p class="text-sm leading-7 text-slate-200">Short-answer recall ko flashcard style me practice karo.</p>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5">
        <h2 class="text-lg font-semibold text-slate-900">Units</h2>
        <div class="mt-4 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none]">
          <button
            v-for="unit in practiceUnits"
            :key="unit.value"
            type="button"
            class="snap-start shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition"
            :class="selectedUnit === unit.value ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'"
            @click="selectedUnit = unit.value; selectedTopic = 'all'"
          >
            {{ unit.label }}
          </button>
        </div>

        <h2 class="mt-5 text-lg font-semibold text-slate-900">Topics</h2>
        <div class="mt-4 flex snap-x snap-mandatory gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [-ms-overflow-style:none]">
          <button
            v-for="topic in topicOptions"
            :key="topic.value"
            type="button"
            class="snap-start shrink-0 rounded-full border px-3 py-1 text-xs font-medium transition"
            :class="selectedTopic === topic.value ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'"
            @click="selectedTopic = topic.value"
          >
            {{ topic.label }}
          </button>
        </div>

        <h2 class="mt-5 text-lg font-semibold text-slate-900">Mode</h2>
        <div class="mt-4 grid grid-cols-2 gap-3">
          <button type="button" class="rounded-2xl border px-4 py-3 text-sm font-medium transition" :class="mode === 'hide' ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'" @click="mode = 'hide'">
            Jawab chhupao
          </button>
          <button type="button" class="rounded-2xl border px-4 py-3 text-sm font-medium transition" :class="mode === 'show' ? 'border-slate-950 bg-slate-950 text-white' : 'border-slate-200 bg-slate-50 text-slate-700'" @click="mode = 'show'">
            Jawab dekho
          </button>
        </div>
      </section>

      <section class="space-y-4 rounded-3xl border border-slate-200 bg-white p-5">
        <h2 class="text-lg font-semibold text-slate-900">Practice Set</h2>
        <div v-for="question in filteredQuestions" :key="question.id" class="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-200">
          <div class="flex gap-2 text-xs font-medium">
            <span class="rounded-full bg-slate-100 px-3 py-1 text-slate-700">{{ question.unitLabel }}</span>
            <span class="rounded-full bg-sky-100 px-3 py-1 text-sky-800">{{ question.topicLabel }}</span>
          </div>
          <p class="mt-3 text-sm font-semibold text-slate-900">{{ question.question }}</p>
          <button
            v-if="mode === 'hide'"
            type="button"
            class="mt-3 rounded-full border border-slate-300 px-3 py-1 text-sm font-medium text-slate-800"
            @click="toggleCard(question.id)"
          >
            {{ openCard === question.id ? "Chhupao" : "Jawab dekho" }}
          </button>
          <p v-if="mode === 'show' || openCard === question.id" class="mt-3 text-sm leading-7 text-slate-700">{{ question.answer }}</p>
          <div class="mt-3 flex flex-wrap gap-3 text-sm">
            <NuxtLink :to="question.conceptTo" class="font-medium text-sky-700 underline decoration-sky-300 underline-offset-4">Yeh topic dobara parho -></NuxtLink>
            <NuxtLink :to="question.chapterTo" class="font-medium text-slate-700 underline decoration-slate-300 underline-offset-4">Chapter page kholo -></NuxtLink>
          </div>
        </div>
      </section>

      <section class="rounded-3xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
        <div>
          <p class="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">FAQ</p>
          <h2 class="mt-2 text-2xl font-semibold tracking-tight text-slate-950 dark:text-white">Practice Short Questions FAQs</h2>
        </div>
        <div class="mt-5 space-y-4">
          <article
            v-for="faq in faqs"
            :key="faq.question"
            class="rounded-[22px] border border-slate-200 bg-slate-50 p-5 dark:border-slate-800 dark:bg-slate-950"
          >
            <h3 class="text-lg font-semibold text-slate-950 dark:text-white">{{ faq.question }}</h3>
            <p class="mt-2 text-sm leading-7 text-slate-600 dark:text-slate-300">{{ faq.answer }}</p>
          </article>
        </div>
      </section>
    </div>
  </PageContainer>
</template>

